import type { BleDevice, GattServer } from '@/ble/types';
import type { Unsubscribe } from '@/ble/gatt';
import { Emitter, type SimParams, type TrainerData, type TrainerKind } from '@/trainer/types';
import { readBattery } from './battery';
import { toHex } from '@/ble/bytes';

/** Shared plumbing for BLE devices: connection state, data/log emitters, disconnect handling. */
export abstract class BleDeviceBase {
  batteryPct: number | null = null;

  protected readonly dataEmitter = new Emitter<TrainerData>();
  protected readonly connEmitter = new Emitter<boolean>();
  protected readonly logEmitter = new Emitter<string>();
  protected unsubs: Unsubscribe[] = [];
  protected server: GattServer | null = null;
  protected last: TrainerData = { t: 0 };
  private isConnected = false;
  private manualDisconnect = false;

  constructor(protected readonly device: BleDevice) {
    device.addEventListener('gattserverdisconnected', this.handleDisconnected);
  }

  get name(): string {
    return this.device.name ?? 'Gerät';
  }

  get deviceId(): string {
    return this.device.id;
  }

  get connected(): boolean {
    return this.isConnected;
  }

  onData(cb: (d: TrainerData) => void): () => void {
    return this.dataEmitter.on(cb);
  }

  onConnectionChange(cb: (connected: boolean) => void): () => void {
    return this.connEmitter.on(cb);
  }

  onLog(cb: (line: string) => void): () => void {
    return this.logEmitter.on(cb);
  }

  async connect(): Promise<void> {
    this.manualDisconnect = false;
    const gatt = this.device.gatt;
    if (!gatt) throw new Error('Device has no GATT server');
    this.server = gatt.connected ? gatt : await gatt.connect();
    this.cleanupSubscriptions();
    await this.setup(this.server);
    this.isConnected = true;
    this.connEmitter.emit(true);
    void readBattery(this.server).then((b) => {
      if (b !== null) this.batteryPct = b;
    });
  }

  async disconnect(): Promise<void> {
    this.manualDisconnect = true;
    this.cleanupSubscriptions();
    this.onTransportLost();
    try {
      this.device.gatt?.disconnect();
    } catch {
      /* ignore */
    }
    if (this.isConnected) {
      this.isConnected = false;
      this.connEmitter.emit(false);
    }
  }

  /** Remove the disconnect listener when the trainer object is discarded. */
  dispose(): void {
    this.device.removeEventListener('gattserverdisconnected', this.handleDisconnected);
  }

  get wasManualDisconnect(): boolean {
    return this.manualDisconnect;
  }

  /** Discover characteristics, subscribe and initialise control. */
  protected abstract setup(server: GattServer): Promise<void>;

  /** Called when the transport goes away (clear queues, timers). */
  protected abstract onTransportLost(): void;

  protected emitData(patch: Partial<TrainerData>): void {
    this.last = { ...this.last, ...patch, t: Date.now() };
    this.dataEmitter.emit(this.last);
  }

  protected log(dir: 'tx' | 'rx' | '--', bytes: Uint8Array | DataView | null, note = ''): void {
    if (this.logEmitter.size === 0) return;
    const hex = bytes ? toHex(bytes) : '';
    this.logEmitter.emit(`${new Date().toISOString().slice(11, 23)} ${dir} ${hex}${note ? `  ${note}` : ''}`);
  }

  private cleanupSubscriptions(): void {
    for (const u of this.unsubs) u();
    this.unsubs = [];
  }

  private readonly handleDisconnected = () => {
    this.onTransportLost();
    this.unsubs = [];
    if (this.isConnected) {
      this.isConnected = false;
      this.connEmitter.emit(false);
    }
  };
}

/** Base for controllable trainers. */
export abstract class BleTrainerBase extends BleDeviceBase {
  abstract readonly kind: TrainerKind;
  caps: { erg: boolean; sim: boolean; powerRange?: [number, number] } = { erg: true, sim: true };
  abstract setErg(watts: number): Promise<void>;
  abstract setSim(p: SimParams): Promise<void>;
}
